/**
 * # Coachmark
 *
 * The announcement callout used to point at one control and say what it does: a filled card
 * anchored to a target, with a caret, a title, a line of copy, and one action. Product tours and
 * first-run hints are what it is for.
 *
 * Pair it with `Spotlight` to dim everything else. The two are separate because the dim is
 * optional — a single hint in a quiet corner of the page rarely needs one.
 *
 * The card is `background/special` in both themes, so its content uses the `-static` inverse
 * colours rather than the theme-reactive ones.
 *
 * ## Props
 *
 * - `target` (HTMLElement | null): the element the caret points at. Renders nothing while `null`.
 *   Take it with a callback ref held in state, not `useRef` — the component has to re-render when
 *   the element appears, and a ref mutation does not do that.
 * - `title` (string): heading, also the dialog's accessible name.
 * - `body` (ReactNode): the copy.
 * - `children` (ReactNode): the action area, right-aligned 12px under the copy. Usually one `Button`.
 * - `placement` (Placement): Popper placement. Default `'bottom'`.
 * - `trapFocus` (boolean): hold keyboard focus inside the card. Default `true`. Turn it off for a
 *   step whose action is the anchored control itself, which the user has to be able to reach.
 * - `className` (string): extra classes for the card.
 *
 * ## Usage
 *
 * ```jsx
 * const [filterButton, setFilterButton] = useState<HTMLElement | null>(null);
 *
 * <button ref={setFilterButton}>Filter students</button>
 * <Coachmark target={filterButton} title='Filter students' body='Narrow the roster.'>
 *   <Button color='blend-dark' onClick={advance}>Next</Button>
 * </Coachmark>
 * ```
 */

import React from 'react';
import FocusLock from 'react-focus-lock';
import type {Placement} from '@popperjs/core';

import Heading from '../../atoms/Heading/Heading.react';
import Text from '../../atoms/Text/Text.react';
import AnchoredFixedPosition from '../../helpers/AnchoredFixedPosition/AnchoredFixedPosition.react';

// The caret is SCSS, against the Tailwind-only rule: its rotation lives on a pseudo-element and
// its offset keys off `data-placement` on an ancestor. Tailwind renders neither — verified.
import './coachmark.scss';

/**
 * Intercom injects its container as a sibling of `#app` and paints above this card, so without the
 * exemption the lock pulls focus straight back out of its composer. Same predicate atomic `Modal`
 * uses. Absent an `#app` to measure against there is nothing to exempt, so the lock keeps its full
 * reach rather than silently becoming a no-op.
 */
const isInsideApp = (node: HTMLElement) => {
  const appRoot = document.getElementById('app');
  return !appRoot || appRoot.contains(node);
};

/**
 * The card is wider than the scroll containers some anchors live in (a grading panel, a sidebar
 * column), and Popper's default boundary is the trigger's scrollable ancestor — which would squeeze
 * or flip the card inside that container. The viewport is the only sensible boundary here.
 */
const VIEWPORT_BOUNDED = {preventOverflow: {boundariesElement: 'viewport'}} as const;

interface Props {
  target: HTMLElement | null;
  title: string;
  body: React.ReactNode;
  children?: React.ReactNode;
  placement?: Placement;
  trapFocus?: boolean;
  className?: string;
  'data-testid'?: string;
}

export const Coachmark = ({
  target,
  title,
  body,
  children,
  placement = 'bottom',
  trapFocus = true,
  className = '',
  'data-testid': testId
}: Props) => {
  if (!target) {
    return null;
  }

  const card = (
    <div
      aria-label={title}
      aria-modal={trapFocus || undefined}
      className={`gap-x0-half background-special elevation-shadow-l3 rounded-x1 p-x2 flex w-[255px] flex-col items-end ${className}`}
      data-testid={testId}
      role='dialog'
    >
      <Heading className='w-full' color='primary-inverse-static' size='xs'>
        {title}
      </Heading>
      <Text className='w-full' color='primary-inverse-static' size='m'>
        {body}
      </Text>
      {/* Takes the card's 4px gap to 12px, so the action sits further from the copy than the
          copy does from its own heading. */}
      {children ? <div className='mt-x1'>{children}</div> : null}
    </div>
  );

  return (
    <AnchoredFixedPosition
      arrowClassName='m-coachmark-caret'
      className='z-popover'
      modifiers={VIEWPORT_BOUNDED}
      placement={placement}
      target={target}
      usePortal
    >
      {/* The lock wraps the dialog rather than being it: `FocusLock` forwards only its `lockProps`
          to the DOM, so attributes written on it directly are dropped. */}
      {trapFocus ? (
        <FocusLock as='div' whiteList={isInsideApp}>
          {card}
        </FocusLock>
      ) : (
        card
      )}
    </AnchoredFixedPosition>
  );
};

export default Coachmark;
